import { Suspense, useState, createContext, useContext, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Package,
  Briefcase,
  User,
  Settings,
  LogOut,
  Menu,
  X,
  ShoppingCart,
  Heart,
  ChevronDown,
  Home,
  ClipboardList,
  LayoutDashboard,
  MessageSquare
} from 'lucide-react';
import { Footer } from '@/components/common/Footer';
import { cn } from '@/lib/utils';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Logo } from '@/components/common/Logo';

import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/constants';
import { Button } from '@/components/ui/button';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchCart, selectCartItemCount } from '@/store/slices/cartSlice';
import { fetchWishlist, selectWishlistItemCount } from '@/store/slices/wishlistSlice';

interface DashboardContextType {
  activeMode: 'services' | 'products';
  setActiveMode: (mode: 'services' | 'products') => void;
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboardContext must be used within DashboardLayout');
  }
  return context;
};

/**
 * Dashboard layout - Amazon/Flipkart inspired with Swiggy-style mode switching
 */
const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  // Determine initial mode based on current route
  const getInitialMode = (): 'services' | 'products' => {
    if (location.pathname.startsWith('/products')) return 'products';
    if (location.pathname.startsWith('/services')) return 'services';
    return 'services'; // default
  };

  const [activeMode, setActiveMode] = useState<'services' | 'products'>(getInitialMode());

  // Update mode when route changes
  useEffect(() => {
    if (location.pathname.startsWith('/products')) {
      setActiveMode('products');
    } else if (location.pathname.startsWith('/services')) {
      setActiveMode('services');
    }
  }, [location.pathname]);
  const dispatch = useAppDispatch();
  const cartItemCount = useAppSelector(selectCartItemCount);
  const wishlistItemCount = useAppSelector(selectWishlistItemCount);

  useEffect(() => {
    if (user) {
      dispatch(fetchCart());
      dispatch(fetchWishlist());
    }
  }, [dispatch, user]);

  const handleModeChange = (mode: 'services' | 'products') => {
    setActiveMode(mode);
    // Navigate to the appropriate page based on mode
    if (mode === 'products') {
      // If on services page or service detail, navigate to products
      if (location.pathname.startsWith('/services')) {
        navigate(ROUTES.PRODUCTS);
      } else if (location.pathname === ROUTES.DASHBOARD) {
        // Stay on dashboard, context will update the view
      } else if (!location.pathname.startsWith('/products')) {
        // Navigate to products page if not already on products
        navigate(ROUTES.PRODUCTS);
      }
    } else {
      // If on products page or product detail, navigate to services
      if (location.pathname.startsWith('/products')) {
        navigate(ROUTES.SERVICES);
      } else if (location.pathname === ROUTES.DASHBOARD) {
        // Stay on dashboard, context will update the view
      } else if (!location.pathname.startsWith('/services')) {
        // Navigate to services page if not already on services
        navigate(ROUTES.SERVICES);
      }
    }
  };

  const isActive = (path: string) => {
    if (path === ROUTES.DASHBOARD) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  // Bottom navigation items
  const bottomNavItems = [
    { label: 'Home', href: ROUTES.DASHBOARD, icon: Home },
    { label: 'Services', href: ROUTES.SERVICES, icon: Briefcase },
    { label: 'Products', href: ROUTES.PRODUCTS, icon: Package },
    { label: 'Orders', href: ROUTES.ORDERS, icon: ClipboardList },
    { label: 'Account', href: ROUTES.PROFILE, icon: User },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Top Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-sm">
        <div className="container mx-auto max-w-7xl px-4">
          <div className="flex h-16 items-center justify-between gap-6">
            {/* Logo */}
            <Link
              to={ROUTES.DASHBOARD}
              className="flex shrink-0 items-center transition-opacity hover:opacity-90"
            >
              <Logo variant="full" size="sm" className="hidden sm:block" linkToHome={false} />
              <Logo variant="single" size="sm" className="sm:hidden" linkToHome={false} />
            </Link>

            {/* Mode Toggle */}
            {user?.role === 'user' && (
              <div className="flex items-center rounded-full bg-muted/80 p-1 shadow-inner">
                <button
                  type="button"
                  onClick={() => handleModeChange('services')}
                  className={cn(
                    'flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-200',
                    activeMode === 'services'
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <Briefcase className="h-4 w-4" />
                  Services
                </button>
                <button
                  type="button"
                  onClick={() => handleModeChange('products')}
                  className={cn(
                    'flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-200',
                    activeMode === 'products'
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <Package className="h-4 w-4" />
                  Products
                </button>
              </div>
            )}

            {/* Right: Icons + User */}
            <div className="flex items-center gap-1 sm:gap-2">
              {user?.role === 'user' && (
                <>
                  <Link to={ROUTES.WISHLIST}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="relative h-10 w-10 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <Heart className="h-5 w-5" />
                      {wishlistItemCount > 0 && (
                        <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
                          {wishlistItemCount > 9 ? '9+' : wishlistItemCount}
                        </span>
                      )}
                    </Button>
                  </Link>
                  <Link to={ROUTES.CART}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="relative h-10 w-10 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <ShoppingCart className="h-5 w-5" />
                      {cartItemCount > 0 && (
                        <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                          {cartItemCount > 9 ? '9+' : cartItemCount}
                        </span>
                      )}
                    </Button>
                  </Link>
                  <div className="mx-1 h-6 w-px bg-border" aria-hidden />
                </>
              )}

              <ThemeToggle className="h-10 w-10 rounded-full" />

              {/* User Menu */}
              <div className="relative ml-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'gap-2 rounded-full pl-1.5 pr-3 font-medium transition-colors',
                    userMenuOpen && 'bg-muted'
                  )}
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  {user?.profile?.avatar ? (
                    <img
                      src={user.profile.avatar}
                      alt=""
                      className="h-8 w-8 rounded-full object-cover ring-2 ring-border"
                    />
                  ) : (
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                      {user?.firstName?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  )}
                  <span className="hidden max-w-[100px] truncate sm:inline text-sm">
                    {user?.firstName}
                  </span>
                  <ChevronDown
                    className={cn('h-4 w-4 shrink-0 transition-transform', userMenuOpen && 'rotate-180')}
                  />
                </Button>

                {userMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setUserMenuOpen(false)}
                      aria-hidden
                    />
                    <div
                      className="absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-xl border bg-card py-1.5 shadow-lg"
                      role="menu"
                    >
                      <div className="px-4 py-3 border-b bg-muted/30">
                        <p className="truncate text-sm font-semibold">
                          {user?.firstName} {user?.lastName}
                        </p>
                        {user?.email && (
                          <p className="truncate text-xs text-muted-foreground">
                            {user.email}
                          </p>
                        )}
                      </div>
                      <div className="py-1">
                        <Link
                          to={ROUTES.DASHBOARD}
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
                        >
                          <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
                          Dashboard
                        </Link>
                        <Link
                          to={ROUTES.ORDERS}
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
                        >
                          <ClipboardList className="h-4 w-4 text-muted-foreground" />
                          My Orders
                        </Link>
                        <Link
                          to={ROUTES.PROFILE}
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
                        >
                          <User className="h-4 w-4 text-muted-foreground" />
                          Profile
                        </Link>
                        <Link
                          to={ROUTES.SETTINGS}
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
                        >
                          <Settings className="h-4 w-4 text-muted-foreground" />
                          Settings
                        </Link>
                        <Link
                          to="/support"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
                        >
                          <MessageSquare className="h-4 w-4 text-muted-foreground" />
                          Support
                        </Link>
                      </div>
                      <div className="border-t py-1">
                        <button
                          type="button"
                          onClick={() => {
                            logout();
                            setUserMenuOpen(false);
                          }}
                          className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-destructive transition-colors hover:bg-destructive/10"
                        >
                          <LogOut className="h-4 w-4" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pb-16 lg:pb-0 flex flex-col">
        <Suspense
          fallback={
            <div className="flex h-[60vh] items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          }
        >
          <DashboardContext.Provider value={{ activeMode, setActiveMode }}>
            <Outlet />
          </DashboardContext.Provider>
        </Suspense>
      </main>

      {/* Footer */}
      <Footer supportPath="/support" />

      {/* Mobile Bottom Navigation */}
      {user?.role === 'user' && (
        <nav className="fixed bottom-0 left-0 right-0 z-50 lg:hidden border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-[0_-1px_3px_rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-around py-2">
            {bottomNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-lg py-2 px-4 min-w-[64px] transition-colors',
                    active ? 'text-primary' : 'text-muted-foreground active:bg-muted'
                  )}
                >
                  <Icon className={cn('h-5 w-5', active && 'text-primary')} />
                  <span className="text-[10px] font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
};

export default DashboardLayout;
